import { useId, type ReactNode } from 'react'
import { cx } from '../cx.js'
import styles from './Section.module.scss'

/**
 * Section — one of the ledger's four sections (M3.2): an `<h2>` with its
 * count, the cards, and one line of reassurance when there are none.
 *
 * The sections are the only categories the product has, and they are
 * typographic — a heading and an order (docs/visual-direction.md). Nothing
 * here sorts, filters or truncates: the caller passes the cards in the
 * ledger's order (attention, M2.7) and the section shows what it is given.
 * Empty, it collapses to its heading and its one line (M3.7 owns the copy).
 * The change between the two states is the only motion — the heading's
 * colour, at the fast duration, instant under reduced motion.
 */
export type SectionProps = {
  /** The heading, verbatim from `LEDGER_SECTIONS` in core. */
  title: string
  /** How many cards follow. Shown beside the heading when there are any. */
  count: number
  /** The one line the section shows when it has no cards. */
  empty: string
  /** A line under the heading, before the cards. */
  note?: string | undefined
  /** A plain line after the cards: "3 more in history." */
  footnote?: string | undefined
  /**
   * The section holds something that is not a card, so it is not empty even
   * with no count: M3.6b's settled lines, where a card was acted on.
   */
  hasContent?: boolean | undefined
  /** The cards, in the ledger's order. */
  children?: ReactNode
}

export function Section({
  title,
  count,
  empty,
  note,
  footnote,
  hasContent,
  children,
}: SectionProps) {
  const headingId = useId()
  const isEmpty = count === 0 && hasContent !== true
  return (
    <section className={cx(styles.root, isEmpty && styles.isEmpty)} aria-labelledby={headingId}>
      <h2 id={headingId} className={styles.heading}>
        {title}
        {count === 0 ? null : (
          <>
            {' '}
            <span className={styles.count}>{count}</span>
          </>
        )}
      </h2>
      {isEmpty ? (
        <p className={styles.empty}>{empty}</p>
      ) : (
        <>
          {note ? <p className={styles.note}>{note}</p> : null}
          <div className={styles.cards}>{children}</div>
          {footnote ? <p className={styles.footnote}>{footnote}</p> : null}
        </>
      )}
    </section>
  )
}
